/**
 * PresenceAnalyticsService
 * Service for Presence Analytics operations
 * Route: api/presenceanalytics
 */
import { BaseService } from '../base/BaseService';
/**
 * PresenceAnalyticsService
 * Handles presence analytics and history operations
 */
export class PresenceAnalyticsService extends BaseService {
    constructor(http, config) {
        super(http, {
            servicePath: config?.servicePath ?? PresenceAnalyticsService.DEFAULT_SERVICE_PATH,
            basePath: PresenceAnalyticsService.BASE_PATH,
            endpoints: config?.endpoints,
        });
    }
    // ==================== CURRENT USER ENDPOINTS ====================
    /**
     * Get current user's presence statistics
     * GET api/presenceanalytics/me/stats
     * @param startDate Optional start date for stats (defaults to 30 days ago on backend)
     * @param endDate Optional end date for stats (defaults to now on backend)
     */
    async getMyStats(startDate, endDate) {
        const response = await this.getMyStatsRaw(startDate, endDate);
        if (!response.data) {
            throw new Error('Failed to fetch user stats');
        }
        return response.data;
    }
    /**
     * Get current user's presence statistics with full response
     */
    getMyStatsRaw(startDate, endDate) {
        const params = {};
        if (startDate)
            params.startDate = startDate.toISOString();
        if (endDate)
            params.endDate = endDate.toISOString();
        const url = this.buildUrlWithParams('me/stats', params);
        return this.http.get(url);
    }
    /**
     * Get current user's presence timeline
     * GET api/presenceanalytics/me/timeline
     * @param startDate Optional start date (defaults to 7 days ago on backend)
     * @param endDate Optional end date (defaults to now on backend)
     * @param limit Maximum number of records (default 100 on backend)
     */
    async getMyTimeline(startDate, endDate, limit) {
        const response = await this.getMyTimelineRaw(startDate, endDate, limit);
        return response.data ?? [];
    }
    /**
     * Get current user's presence timeline with full response
     */
    getMyTimelineRaw(startDate, endDate, limit) {
        const params = {};
        if (startDate)
            params.startDate = startDate.toISOString();
        if (endDate)
            params.endDate = endDate.toISOString();
        if (limit)
            params.limit = limit.toString();
        const url = this.buildUrlWithParams('me/timeline', params);
        return this.http.get(url);
    }
    /**
     * Get current user's presence heatmap
     * GET api/presenceanalytics/me/heatmap
     * @param startDate Optional start date (defaults to 30 days ago on backend)
     * @param endDate Optional end date (defaults to now on backend)
     */
    async getMyHeatmap(startDate, endDate) {
        const response = await this.getMyHeatmapRaw(startDate, endDate);
        if (!response.data) {
            throw new Error('Failed to fetch user heatmap');
        }
        return response.data;
    }
    /**
     * Get current user's presence heatmap with full response
     */
    getMyHeatmapRaw(startDate, endDate) {
        const params = {};
        if (startDate)
            params.startDate = startDate.toISOString();
        if (endDate)
            params.endDate = endDate.toISOString();
        const url = this.buildUrlWithParams('me/heatmap', params);
        return this.http.get(url);
    }
    // ==================== ADMIN USER-SPECIFIC ENDPOINTS ====================
    /**
     * Get specific user's presence statistics (Admin only)
     * GET api/presenceanalytics/users/{userId}/stats
     * Requires Admin or SuperAdmin role
     * @param userId User ID to query
     * @param startDate Optional start date for stats (defaults to 30 days ago on backend)
     * @param endDate Optional end date for stats (defaults to now on backend)
     */
    async getUserStats(userId, startDate, endDate) {
        const response = await this.getUserStatsRaw(userId, startDate, endDate);
        if (!response.data) {
            throw new Error(`Failed to fetch stats for user ${userId}`);
        }
        return response.data;
    }
    /**
     * Get specific user's presence statistics with full response (Admin only)
     */
    getUserStatsRaw(userId, startDate, endDate) {
        const params = {};
        if (startDate)
            params.startDate = startDate.toISOString();
        if (endDate)
            params.endDate = endDate.toISOString();
        const url = this.buildUrlWithParams(`users/${userId}/stats`, params);
        return this.http.get(url);
    }
    /**
     * Get specific user's presence timeline (Admin only)
     * GET api/presenceanalytics/users/{userId}/timeline
     * Requires Admin or SuperAdmin role
     * @param userId User ID to query
     * @param startDate Optional start date (defaults to 7 days ago on backend)
     * @param endDate Optional end date (defaults to now on backend)
     * @param limit Maximum number of records (default 100 on backend)
     */
    async getUserTimeline(userId, startDate, endDate, limit) {
        const response = await this.getUserTimelineRaw(userId, startDate, endDate, limit);
        return response.data ?? [];
    }
    /**
     * Get specific user's presence timeline with full response (Admin only)
     */
    getUserTimelineRaw(userId, startDate, endDate, limit) {
        const params = {};
        if (startDate)
            params.startDate = startDate.toISOString();
        if (endDate)
            params.endDate = endDate.toISOString();
        if (limit)
            params.limit = limit.toString();
        const url = this.buildUrlWithParams(`users/${userId}/timeline`, params);
        return this.http.get(url);
    }
    // ==================== ADMIN SYSTEM-WIDE ENDPOINTS ====================
    /**
     * Get system-wide presence statistics (Admin only)
     * GET api/presenceanalytics/system/stats
     * Requires Admin or SuperAdmin role
     * @param startDate Optional start date for stats (defaults to 30 days ago on backend)
     * @param endDate Optional end date for stats (defaults to now on backend)
     */
    async getSystemStats(startDate, endDate) {
        const response = await this.getSystemStatsRaw(startDate, endDate);
        if (!response.data) {
            throw new Error('Failed to fetch system stats');
        }
        return response.data;
    }
    /**
     * Get system-wide presence statistics with full response (Admin only)
     */
    getSystemStatsRaw(startDate, endDate) {
        const params = {};
        if (startDate)
            params.startDate = startDate.toISOString();
        if (endDate)
            params.endDate = endDate.toISOString();
        const url = this.buildUrlWithParams('system/stats', params);
        return this.http.get(url);
    }
    /**
     * Get most active users (Admin only)
     * GET api/presenceanalytics/system/most-active
     * Requires Admin or SuperAdmin role
     * @param startDate Optional start date (defaults to 7 days ago on backend)
     * @param endDate Optional end date (defaults to now on backend)
     * @param limit Number of users to return (default 10 on backend)
     */
    async getMostActiveUsers(startDate, endDate, limit) {
        const response = await this.getMostActiveUsersRaw(startDate, endDate, limit);
        return response.data ?? [];
    }
    /**
     * Get most active users with full response (Admin only)
     */
    getMostActiveUsersRaw(startDate, endDate, limit) {
        const params = {};
        if (startDate)
            params.startDate = startDate.toISOString();
        if (endDate)
            params.endDate = endDate.toISOString();
        if (limit)
            params.limit = limit.toString();
        const url = this.buildUrlWithParams('system/most-active', params);
        return this.http.get(url);
    }
    /**
     * Get system-wide presence heatmap (Admin only)
     * GET api/presenceanalytics/system/heatmap
     * Requires Admin or SuperAdmin role
     * @param startDate Optional start date (defaults to 30 days ago on backend)
     * @param endDate Optional end date (defaults to now on backend)
     */
    async getSystemHeatmap(startDate, endDate) {
        const response = await this.getSystemHeatmapRaw(startDate, endDate);
        if (!response.data) {
            throw new Error('Failed to fetch system heatmap');
        }
        return response.data;
    }
    /**
     * Get system-wide presence heatmap with full response (Admin only)
     */
    getSystemHeatmapRaw(startDate, endDate) {
        const params = {};
        if (startDate)
            params.startDate = startDate.toISOString();
        if (endDate)
            params.endDate = endDate.toISOString();
        const url = this.buildUrlWithParams('system/heatmap', params);
        return this.http.get(url);
    }
}
PresenceAnalyticsService.DEFAULT_SERVICE_PATH = 'users';
PresenceAnalyticsService.BASE_PATH = 'api/presenceanalytics';
//# sourceMappingURL=PresenceAnalyticsService.js.map